import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Lead } from '../../types';
import { leadsService } from '../../data/leadsService';

interface LeadState {
  leads: Lead[];
  selectedLead: Lead | null;
  loading: boolean;
  error: string | null;
  totalCounts: number;
  summary: {
    total: number;
    active: number;
    qualified: number;
    converted: number;
    lost: number;
  };
  pagination: {
    page: number;
    limit: number;
    pages: number;
    total: number;
  };
}

const initialState: LeadState = {
  leads: [],
  selectedLead: null,
  loading: false,
  error: null,
  totalCounts: 0,
  summary: {
    total: 0,
    active: 0,
    qualified: 0,
    converted: 0,
    lost: 0,
  },
  pagination: {
    page: 1,
    limit: 12,
    pages: 1,
    total: 0,
  },
};

// Async Thunks
export const fetchLeads = createAsyncThunk(
  'leads/fetchLeads',
  async (params: { page?: number; limit?: number; search?: string; status?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await leadsService.getLeads(params.page, params.limit, params.search, params.status);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.error?.message || 'Failed to fetch leads');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch leads');
    }
  }
);

export const fetchLeadById = createAsyncThunk(
  'leads/fetchLeadById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await leadsService.getLead(id);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.error?.message || 'Failed to fetch lead');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch lead');
    }
  }
);

export const createLead = createAsyncThunk(
  'leads/createLead',
  async (leadData: Partial<Lead> & { contactId?: string, name?: string, phone?: string, email?: string }, { rejectWithValue }) => {
    try {
      const response = await leadsService.createLead(leadData);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.error?.message || 'Failed to create lead');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create lead');
    }
  }
);

export const updateLead = createAsyncThunk(
  'leads/updateLead',
  async ({ id, data }: { id: string; data: Partial<Lead> }, { rejectWithValue }) => {
    try {
      const response = await leadsService.updateLead(id, data);
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue(response.error?.message || 'Failed to update lead');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update lead');
    }
  }
);

export const deleteLead = createAsyncThunk(
  'leads/deleteLead',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await leadsService.deleteLead(id);
      if (response.success) {
        return id;
      }
      return rejectWithValue(response.error?.message || 'Failed to delete lead');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete lead');
    }
  }
);

export const convertLead = createAsyncThunk(
  'leads/convertLead',
  async ({ id, contactPersonName }: { id: string; contactPersonName?: string }, { rejectWithValue }) => {
    try {
      const response = await leadsService.convertLead(id, contactPersonName ? { contactPersonName } : undefined);
      console.log("response===>", response);
      if (response.success) {
        return { id, result: response.data };
      }
      return rejectWithValue(response.error?.message || 'Failed to convert lead');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to convert lead');
    }
  }
);


const leadSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    setSelectedLead: (state, action: PayloadAction<Lead | null>) => {
      state.selectedLead = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch Leads
    builder.addCase(fetchLeads.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchLeads.fulfilled, (state, action) => {
      state.loading = false;
      state.leads = action.payload.leads;
      state.totalCounts = action.payload.pagination.total;
      state.pagination = action.payload.pagination;
      if (action.payload.summary) {
        state.summary = action.payload.summary;
      }
    });
    builder.addCase(fetchLeads.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Lead By ID
    builder.addCase(fetchLeadById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchLeadById.fulfilled, (state, action) => {
      state.loading = false;
      state.selectedLead = action.payload;
    });
    builder.addCase(fetchLeadById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create Lead
    builder.addCase(createLead.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createLead.fulfilled, (state, action) => {
      state.loading = false;
      state.leads.unshift(action.payload);
      state.totalCounts += 1;
    });
    builder.addCase(createLead.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update Lead
    builder.addCase(updateLead.pending, (state) => {
      state.error = null;
    });
    builder.addCase(updateLead.fulfilled, (state, action) => {
      const index = state.leads.findIndex(l => l.id === action.payload.id);
      if (index !== -1) {
        state.leads[index] = action.payload;
      }
      if (state.selectedLead?.id === action.payload.id) {
        state.selectedLead = action.payload;
      }
    });
    builder.addCase(updateLead.rejected, (state, action) => {
      state.error = action.payload as string;
    });

    // Delete Lead
    builder.addCase(deleteLead.fulfilled, (state, action) => {
      state.leads = state.leads.filter(l => l.id !== action.payload);
      if (state.selectedLead?.id === action.payload) {
        state.selectedLead = null;
      }
      state.totalCounts -= 1;
    });

    // Convert Lead
    builder.addCase(convertLead.fulfilled, (state, action) => {
      const index = state.leads.findIndex(l => l.id === action.payload.id);
      if (index !== -1) {
        state.leads[index] = { ...state.leads[index], status: 'converted' };
      }
      if (state.selectedLead?.id === action.payload.id) {
        state.selectedLead = { ...state.selectedLead, status: 'converted' };
      }
    });
  }
});

export const { setSelectedLead, clearError } = leadSlice.actions;

// Selectors
export const selectSelectedLead = (state: { leads: LeadState }) => state.leads.selectedLead;
export const selectLeadLoading = (state: { leads: LeadState }) => state.leads.loading;
export const selectLeadError = (state: { leads: LeadState }) => state.leads.error;
export const selectAllLeads = (state: { leads: LeadState }) => state.leads.leads;
export const selectLeadPagination = (state: { leads: LeadState }) => state.leads.pagination;
export const selectLeadTotalCounts = (state: { leads: LeadState }) => state.leads.totalCounts;
export const selectLeadSummary = (state: { leads: LeadState }) => state.leads.summary;

export default leadSlice.reducer;

